import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import { BsCalculator } from 'react-icons/bs';
import { FaCalculator } from 'react-icons/fa';
import TaxPrerequisitesDialog from 'components/dialogs/TaxPrerequisitesDialog';
import { useUpdateData, useFetchActionData } from 'services/queries';
import toast from 'react-hot-toast';
import { useTheme } from '@mui/material/styles';

/**
 * NoTaxCalculationPrompt - Component shown when no tax calculation exists for an order
 * @param {Object} props - Component props
 * @param {Object} props.order - Order data
 * @param {Function} props.onCalculationComplete - Callback when calculation is complete
 */
const NoTaxCalculationPrompt = ({ order, onCalculationComplete }) => {
  const theme = useTheme();
  const [isChecking, setIsChecking] = useState(false);
  const [prerequisites, setPrerequisites] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [orderId, setOrderId] = useState(null);
  
  // Create a function to calculate taxes
  const calculateTaxes = useUpdateData('tax/order-tax-calculation/calculate');
  
  // Use the new function for direct API calls
  const {
    data: prereqData,
    isLoading: isPrereqLoading,
    error: prereqError,
  } = useFetchActionData(orderId ? `tax/prerequisites/${orderId}/` : null);
  
  // Function to check prerequisites before calculating taxes
  const checkPrerequisites = async (id) => {
    setIsChecking(true);
    setOrderId(id); // This will trigger the prerequisites fetch
  };
  
  // Watch for changes in prereqData
  useEffect(() => {
    if (!isPrereqLoading && prereqData && orderId) {
      setIsChecking(false);
      
      if (!prereqData.can_calculate) {
        // Show prerequisites dialog with detailed information
        setPrerequisites(prereqData);
        setOpenDialog(true);
      } else {
        // If prerequisites are met, proceed with calculation
        calculateTaxes.mutate(
          { id: orderId },
          {
            onSuccess: () => {
              toast.success("Tax calculation started successfully");
              // Call the callback to refresh the parent component
              if (onCalculationComplete) {
                setTimeout(() => {
                  onCalculationComplete();
                }, 1000);
              }
            },
            onError: (error) => {
              toast.error(`Failed to calculate taxes: ${error.message}`);
            }
          }
        );
      }
      setOrderId(null); // Reset orderId after processing
    }
  }, [prereqData, isPrereqLoading, orderId]);
  
  useEffect(() => {
    if (prereqError) {
      setIsChecking(false);
      toast.error("Failed to check prerequisites");
      setOrderId(null);
    }
  }, [prereqError]);
  
  const handleCalculateTaxes = (id) => {
    checkPrerequisites(id);
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setPrerequisites(null);
  };

  return (
    <>
      <Box p={2}>
        <Card
          sx={{
            p: 4,
            textAlign: 'center',
            border: `2px dashed ${theme.palette.divider}`,
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
            boxShadow: 'none',
            borderRadius: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            }
          }}
        >
          <FaCalculator 
            size={48} 
            color={theme.palette.text.secondary}
            style={{ marginBottom: 16 }}
          />
          
          <Typography variant="h6" gutterBottom color="text.secondary">
            No Tax Calculation Available
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Tax calculations need to be run before viewing the detailed breakdown.
            Click the button below to calculate taxes for this order.
          </Typography>
          
          <Button
            variant="contained"
            size="large"
            startIcon={isChecking ? <CircularProgress size={20} color="inherit" /> : <BsCalculator />}
            onClick={() => handleCalculateTaxes(order.id)}
            disabled={isChecking}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                transform: 'translateY(-1px)',
                boxShadow: theme.shadows[4],
              },
              transition: 'all 0.2s ease',
            }}
          >
            {isChecking ? 'Checking Prerequisites...' : 'Calculate Taxes'}
          </Button>
        </Card>
      </Box>

      {/* Prerequisites Dialog */}
      <TaxPrerequisitesDialog
        open={openDialog}
        onClose={handleCloseDialog}
        prerequisites={prerequisites}
      />
    </>
  );
};

export default NoTaxCalculationPrompt;